import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Save,
  ShieldCheck,
} from "lucide-react";
import { toast } from "react-hot-toast";

import permissionsService, {
  Permission,
} from "../../services/permissions.service";
import rolesService from "../../services/roles.service";
import Loader from "../../widgets/Loader";
import { SearchFilter, Dropdown, DropdownOption } from "./ui";
import Can from "../../components/auth/Can";

interface RoleOption {
  _id: string;
  role_key: string;
  role_name: string;
}

const UNGROUPED = "Other";

function RolePermissions() {
  const navigate = useNavigate();
  const { role_key: routeRoleKey } = useParams<{ role_key: string }>();

  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [activeRoleKey, setActiveRoleKey] = useState<string>(
    routeRoleKey || "",
  );
  const [activeRoleName, setActiveRoleName] = useState<string>("");

  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [originalSelected, setOriginalSelected] = useState<Set<string>>(
    new Set(),
  );

  const [search, setSearch] = useState<string>("");
  const [view, setView] = useState<"all" | "assigned" | "available">("all");
  const [openModules, setOpenModules] = useState<Set<string>>(new Set());

  const [isLoadingRoles, setIsLoadingRoles] = useState<boolean>(true);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // ── load roles + permissions catalog once ──
  useEffect(() => {
    (async () => {
      try {
        setIsLoadingRoles(true);
        const [rolesRes, permsRes] = await Promise.all([
          rolesService.getRolesDropdown(),
          permissionsService.listPermissions({ skip: 0, limit: 500 }),
        ]);
        setRoles(rolesRes || []);
        setPermissions(permsRes?.data?.permissions || []);
      } catch (e: any) {
        toast.error(
          e?.response?.data?.error?.error_description ||
            "Failed to load roles/permissions",
        );
      } finally {
        setIsLoadingRoles(false);
      }
    })();
  }, []);

  // ── whenever activeRoleKey changes, fetch its assigned permissions ──
  useEffect(() => {
    if (!activeRoleKey) return;
    (async () => {
      try {
        setIsLoadingData(true);
        const data = await permissionsService.getRolePermissions(activeRoleKey);
        const set = new Set<string>(data?.permissions || []);
        setSelected(set);
        setOriginalSelected(new Set(set));
        setActiveRoleName(data?.role_name || "");
      } catch (e: any) {
        toast.error(
          e?.response?.data?.error?.error_description ||
            "Failed to load role permissions",
        );
        setSelected(new Set());
        setOriginalSelected(new Set());
      } finally {
        setIsLoadingData(false);
      }
    })();
  }, [activeRoleKey]);

  // ── derived: group permissions by meta_data.module, filter by view + search ──
  const filteredGrouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    let filtered = permissions;

    // 1) filter by view (assigned vs available)
    if (view === "assigned") {
      filtered = filtered.filter((p) => selected.has(p.permission_key));
    } else if (view === "available") {
      filtered = filtered.filter((p) => !selected.has(p.permission_key));
    }

    // 2) filter by free-text search
    if (q) {
      filtered = filtered.filter((p) =>
        [
          p.permission_key,
          p.permission_name,
          p.description,
          p.meta_data?.module,
        ]
          .filter(Boolean)
          .some((s) => String(s).toLowerCase().includes(q)),
      );
    }

    const map = new Map<string, Permission[]>();
    for (const p of filtered) {
      const mod = p.meta_data?.module || UNGROUPED;
      if (!map.has(mod)) map.set(mod, []);
      map.get(mod)!.push(p);
    }
    // keep the seed-file order — already returned by backend (createdAt asc).
    return Array.from(map.entries());
  }, [permissions, search]);

  // ── default-open all modules whenever the visible set changes ──
  useEffect(() => {
    setOpenModules(new Set(filteredGrouped.map(([m]) => m)));
  }, [filteredGrouped.length, search, view]);

  const dirty = useMemo(() => {
    if (selected.size !== originalSelected.size) return true;
    for (const k of selected) if (!originalSelected.has(k)) return true;
    return false;
  }, [selected, originalSelected]);

  // ── handlers ──
  const togglePermission = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleModule = (module: string) => {
    setOpenModules((prev) => {
      const next = new Set(prev);
      if (next.has(module)) next.delete(module);
      else next.add(module);
      return next;
    });
  };

  const toggleSelectAllInModule = (
    modulePerms: Permission[],
    allOn: boolean,
  ) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const p of modulePerms) {
        if (allOn) next.delete(p.permission_key);
        else next.add(p.permission_key);
      }
      return next;
    });
  };

  const handleRoleChange = (newKey: string) => {
    setActiveRoleKey(newKey);
    // The route's :role_key is the source of truth; navigating updates the URL.
    navigate(`/roles/${newKey}/permissions`, { replace: true });
  };

  const handleSave = async () => {
    if (!activeRoleKey) return;
    try {
      setIsSaving(true);
      await permissionsService.assignPermissionsToRole({
        role_key: activeRoleKey,
        permissions: Array.from(selected),
      });
      setOriginalSelected(new Set(selected));
      toast.success("Permissions updated");
    } catch (e: any) {
      toast.error(
        e?.response?.data?.error?.error_description ||
          "Failed to update permissions",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetToOriginal = () => {
    setSelected(new Set(originalSelected));
  };

  // ── render ──
  if (isLoadingRoles) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  const totalSelected = selected.size;
  const totalPermissions = permissions.length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-slate-200 border-b mb-4">
        <div className="px-6 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/roles")}
                className="text-gray-500 hover:text-gray-800 p-1 rounded"
                title="Back to Roles"
                type="button"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <ShieldCheck className="w-6 h-6 text-blue-600" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  Role Permissions
                  {activeRoleName && (
                    <span className="ml-2 inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full align-middle">
                      {activeRoleName}
                    </span>
                  )}
                </h1>
                <p className="text-xs text-slate-600">
                  {totalSelected} of {totalPermissions} permissions assigned
                  {dirty && (
                    <span className="ml-2 text-amber-600 font-medium">
                      • unsaved changes
                    </span>
                  )}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 items-center justify-end">
              <Can permission="roles:edit">
              <button
                onClick={handleResetToOriginal}
                disabled={!dirty || isSaving}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                type="button"
              >
                Reset
              </button>
              <button
                onClick={handleSave}
                disabled={!dirty || isSaving || !activeRoleKey}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                type="button"
              >
                <Save className="w-4 h-4" />
                {isSaving ? "Saving…" : "Save"}
              </button>
              </Can>
              <Can permission="roles:edit" loading={null} fallback={
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 rounded-md">
                  Read-only · roles:edit required
                </span>
              }>
                {null}
              </Can>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="pb-6">
        {!activeRoleKey ? (
          <div className="bg-white rounded-md border border-gray-200 p-12 text-center text-gray-500">
            Select a role from the dropdown below to manage its permissions.
          </div>
        ) : (
          <div className="space-y-4">
            {/* Top Controls Card */}
            <div className="bg-white rounded-md border border-gray-200 p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4">
                <div className="w-full sm:w-1/3">
                  <label className="text-xs font-semibold text-gray-500 uppercase block mb-1">
                    Select Role <span className="text-red-500">*</span>
                  </label>
                  <Dropdown
                    options={roles.map<DropdownOption>((r) => ({
                      value: r.role_key,
                      label: r.role_name,
                      description: r.role_key,
                    }))}
                    value={activeRoleKey}
                    onChange={handleRoleChange}
                    placeholder="Select a role…"
                    searchable
                    className="w-full"
                    ariaLabel="Select role"
                  />
                </div>

                <div className="w-full sm:w-1/4">
                  <label className="text-xs font-semibold text-gray-500 uppercase block mb-1">
                    View
                  </label>
                  <Dropdown
                    options={[
                      { value: "all",       label: "All permissions",       description: `${totalPermissions} total` },
                      { value: "assigned",  label: "Assigned only",         description: `${totalSelected} assigned` },
                      { value: "available", label: "Available to assign",   description: `${totalPermissions - totalSelected} available` },
                    ]}
                    value={view}
                    onChange={(v) => setView(v as "all" | "assigned" | "available")}
                    placeholder="Choose a view"
                    className="w-full"
                    ariaLabel="Filter permissions by assignment status"
                  />
                </div>

                <div className="w-full sm:flex-1 mt-auto sm:mt-0">
                  <label className="text-xs font-semibold text-gray-500 uppercase block mb-1">
                    Search
                  </label>
                  <SearchFilter
                    placeholder="Search by name, key, or module…"
                    value={search}
                    onChange={setSearch}
                    debounceMs={250}
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            {/* Permission List Card */}
            {isLoadingData ? (
              <div className="bg-white rounded-md border border-gray-200 p-12 text-center">
                <Loader />
              </div>
            ) : filteredGrouped.length === 0 ? (
              <div className="bg-white rounded-md border border-gray-200 p-12 text-center text-gray-500">
                {search
                  ? "No permissions match your search."
                  : "No permissions exist yet."}
              </div>
            ) : (
              <div className="bg-white rounded-md border border-gray-200 overflow-hidden">
                {/* List Header */}
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200 text-sm text-gray-700">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      checked={
                        selected.size === permissions.length &&
                        permissions.length > 0
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelected(
                            new Set(permissions.map((p) => p.permission_key)),
                          );
                        } else {
                          setSelected(new Set());
                        }
                      }}
                    />
                    <span className="font-medium">Select All</span>
                  </div>
                  <button
                    onClick={() => {
                      if (openModules.size === filteredGrouped.length) {
                        setOpenModules(new Set());
                      } else {
                        setOpenModules(
                          new Set(filteredGrouped.map(([m]) => m)),
                        );
                      }
                    }}
                    className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 font-medium"
                    type="button"
                  >
                    <ChevronDown
                      className={`w-4 h-4 transform transition-transform ${openModules.size === filteredGrouped.length ? "rotate-180" : ""}`}
                    />
                    {openModules.size === filteredGrouped.length
                      ? "Collapse All"
                      : "Expand All"}
                  </button>
                </div>

                {/* List Body */}
                <div className="divide-y divide-gray-100">
                  {filteredGrouped.map(([module, modulePerms]) => {
                    const moduleSelectedCount = modulePerms.filter((p) =>
                      selected.has(p.permission_key),
                    ).length;
                    const allOn = moduleSelectedCount === modulePerms.length;
                    const isOpen = openModules.has(module);

                    return (
                      <div
                        key={module}
                        className="border-b border-gray-100 last:border-b-0"
                      >
                        {/* Module Header */}
                        <div
                          className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 cursor-pointer"
                          onClick={() => toggleModule(module)}
                        >
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              checked={allOn}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleSelectAllInModule(modulePerms, allOn);
                              }}
                            />
                            {isOpen ? (
                              <ChevronDown className="w-4 h-4 text-gray-500" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-500" />
                            )}
                            <ShieldCheck className="w-4 h-4 text-blue-600" />
                            <span className="font-semibold text-gray-900 text-sm">
                              {module}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 font-medium bg-gray-100 px-2 py-0.5 rounded-full">
                            {modulePerms.length} permissions
                          </div>
                        </div>

                        {/* Module Body (Permissions) */}
                        {isOpen && (
                          <div className="bg-white">
                            {modulePerms.map((p) => {
                              const checked = selected.has(p.permission_key);
                              return (
                                <div
                                  key={p._id}
                                  className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 pl-12 border-t border-gray-50"
                                >
                                  <div className="flex items-center gap-3">
                                    <input
                                      id={`perm-${p._id}`}
                                      type="checkbox"
                                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                      checked={checked}
                                      onChange={() =>
                                        togglePermission(p.permission_key)
                                      }
                                    />
                                    <label
                                      htmlFor={`perm-${p._id}`}
                                      className="text-sm text-gray-700 cursor-pointer"
                                    >
                                      {p.permission_name}
                                    </label>
                                  </div>
                                  <div className="text-xs text-gray-500 font-medium font-mono">
                                    {checked ? "1/1" : "0/1"}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default RolePermissions;
